import * as React from 'react';
import { useDispatch } from 'react-redux';
import { Flex, Box, flameTheme } from '@lightspeed/flame/Core';
import { Card } from '@lightspeed/flame/Card';
import { Button } from '@lightspeed/flame/Button';
import { Divider } from '@lightspeed/flame/Divider';
import { Text } from '@lightspeed/flame/Text';
import Client from 'pages/views/devices/socketClient';
import { setHubEnabled } from 'pages/views/devices/utils/storage';
import { ConnectionLine, BASE } from 'pages/views/devices/components/ConnectionLine';
import { DownloadAppElement } from 'pages/views/devices/components/DownloadAppElement';
import { useGetAppDownloadUrl } from 'pages/views/devices/hooks/use-get-app-download-url';
import {
  PROTOCOL,
  PROTOCOL_IFRAME,
  APP_NAME,
  getDetectedPlatform,
} from 'pages/views/devices/utils/helper';
import { installationSteps, Step } from './installationSteps';
import { AlternatePlatform } from './AlternatePlatform';

const InstallationPage: React.FC = () => {
  const { colors, fontSizes } = flameTheme;
  const { platform } = getDetectedPlatform();
  const { appDownloadLinks } = useGetAppDownloadUrl();

  const dispatch = useDispatch();

  const handleOpenAppClick = () => {
    setHubEnabled();
    Client.connect();
    dispatch({
      type: 'HUB_UPDATE_DOWNLOAD_STATUS',
      payload: { hasStartedHubDownload: false },
    });
  };

  if (platform === null) return <Text>Only Mac and Windows operating systems are supported</Text>;

  return (
    <Flex p={3} flexDirection="column" justifyContent="flex-start">
      <Text fontSize={fontSizes.large} color={colors.textHeading}>
        Downloading {`${APP_NAME}`} for{' '}
        <span style={{ textTransform: 'capitalize' }}>{platform?.toLowerCase()}</span>
      </Text>
      <Flex mt={3}>
        <Text>Having issues with the download?</Text>
        <DownloadAppElement url={appDownloadLinks?.[platform]} content="Try again" mr={1} ml={1} />
        <AlternatePlatform platform={platform} />
      </Flex>
      <Divider mt={3} mb={3} />
      <Flex flexDirection="column">
        <Flex justifyContent="center">
          {installationSteps[platform]?.map((step: Step) => (
            <Flex
              key={step.action}
              flexDirection="column"
              alignItems="center"
              justifyContent="space-between"
              flex="1 1 0px"
              height="250px"
              p={3}
            >
              <Text fontWeight="bold">{step.action}</Text>
              <Box height="70%" p={3}>
                <img src={step.imageUrl} alt={step.action} style={{ maxHeight: '100%' }} />
              </Box>
              <Text textAlign="center">{step.description}</Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
      <Divider mt={6} mb={6} />
      <Card>
        <Flex justifyContent="space-between" p={6}>
          <Box flex="1">
            <img
              src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-9919532/images/devices/retail-window.png"
              alt="Retail browser window"
              height="140px"
              width="auto"
            />
          </Box>
          <Flex flex="2" flexDirection="column">
            <Text textAlign="center" fontWeight="bold" mb={3}>
              After installing, connect here
            </Text>
            <Flex justifyContent="space-between" alignItems="center">
              <ConnectionLine base={BASE.LEFT} m={3} flex="1" />
              <Button
                style={{ color: colors.white }}
                href={`${PROTOCOL}${Client.getCFDParams()}`}
                target={PROTOCOL_IFRAME}
                onClick={handleOpenAppClick}
                data-test="connect-hub"
                variant="secondary"
                fill
                size="small"
                mb={1}
                noSpacing
              >
                Connect to Hub
              </Button>
              <ConnectionLine base={BASE.RIGHT} m={3} flex="1" />
            </Flex>
          </Flex>
          <Box flex="1">
            <img
              src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-9919532/images/devices/group.png"
              alt="Group of devices"
              height="140px"
              width="auto"
            />
          </Box>
        </Flex>
      </Card>
    </Flex>
  );
};

export { InstallationPage };
